import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, CheckCircle2, CreditCard } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Onboarding() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/loueur/connect/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startOnboarding = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/loueur/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setLoading(false); }
  };

  const verified = status?.charges_enabled;

  return (
    <div className="section-sand min-h-[80vh] py-14">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-3xl shadow-card border border-border p-8 sm:p-10 text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${verified ? "bg-emeraude/15" : "bg-canard/10"}`}>
            {verified ? <CheckCircle2 className="w-11 h-11 text-emeraude" /> : <ShieldCheck className="w-11 h-11 text-canard" />}
          </div>
          <h1 className="font-heading font-extrabold text-2xl text-ink">{t("dash.kycTitle")}</h1>
          <p className="text-muted2 font-body mt-3">{verified ? t("dash.kycVerifiedText") : t("dash.kycPendingText")}</p>

          <div className="bg-sand rounded-xl p-4 mt-6 text-left text-sm font-body space-y-2">
            <StatusRow label={t("dash.stripeConnected")} ok={status?.connected} t={t} />
            <StatusRow label={t("dash.infoSubmitted")} ok={status?.details_submitted} t={t} />
            <StatusRow label={t("dash.chargesEnabled")} ok={status?.charges_enabled} t={t} />
            <StatusRow label={t("dash.payoutsEnabled")} ok={status?.payouts_enabled} t={t} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            {!verified && (
              <Button data-testid="start-onboarding-button" disabled={loading} onClick={startOnboarding} className="flex-1 h-12 rounded-full bg-emeraude hover:bg-emeraude-dark shadow-cta font-bold">
                <CreditCard className="w-5 h-5 mr-2" /> {status?.connected ? t("dash.continueVerif") : t("dash.startVerif")}
              </Button>
            )}
            <Button variant={verified ? "default" : "outline"} data-testid="onboarding-dashboard-button" onClick={() => nav("/loueur")} className={`flex-1 h-12 rounded-full font-bold ${verified ? "bg-canard hover:bg-canard-dark" : ""}`}>
              {t("dash.goDashboard")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <p className="text-xs text-muted2 mt-4">{t("dash.commissionNote")}</p>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, ok, t }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted2">{label}</span>
      <span className={`font-bold ${ok ? "text-emeraude-dark" : "text-yellow-600"}`}>{ok ? `✓ ${t("dash.ok")}` : t("dash.pending")}</span>
    </div>
  );
}
